import { useBacktrack } from 'components/common/withBacktrack';
import { withRedirect } from 'components/common/withRedirect';
import { composeHOCs } from 'shared/utils/composeHOCs';
import { Spinner } from 'components/core';
import { useDialog } from 'components/core/Dialog/common/DialogContext';
import { MemoSelectProjectForm } from 'components/project/Select';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { useCurrentUser } from 'store/user';
import { useProjectActions, useProjectId } from 'store/project';
import { syncUserAndProjectsList } from 'shared/domain/project/refreshUserAndProjectsList';
import { ProjectModel } from 'shared/domain/project/types/model';
import { Alert, Box, Button } from '@mui/material';
import { withCreateProject } from 'components/dataCreationForms/project/withCreateProject';
import { useOrganizations, useOrganizationsActions } from 'store/organizations';
import { useProjects, useProjectsActions } from 'store/projects';
import {
  useAppUrlParams,
  UrlParamName,
} from 'hooks/navigation/useAppUrlParams';
import { debugLog } from 'shared/logger/debugLog';
import { HashMap } from 'shared/types/commonView';
import { transformArrayToHashMap } from 'shared/utils/array';
import { CreateProjectButton } from '../wizard/createProjectButton';
import { compareProjectNames } from 'shared/domain/project/compareProjectNames';
import { shouldAutoSelect } from './model';
import {
  changeConfirmationDialog,
  selectionConfirmationDialog,
} from './selectionConfirmationDialog';
import { filterProjectsForUser } from 'shared/domain/project/filterProjectsForUser';
import { useIsOffline } from 'components/common/withIsOffline';
import { FormattedMessage } from 'intl';
import { CommonTranslationKeys, NotificationTranslationKeys } from 'intl/keys';
import { useProjectPickerSyncState } from './useProjectPickerSyncState';
import { useUserStore } from 'store/user';

function SelectProjectView(): ReactElement {
  const currentUser = useCurrentUser();
  const { authenticate } = useUserStore.use.actions();

  useEffect(() => {
    syncUserAndProjectsList(authenticate);
  }, [authenticate]);

  const { back } = useBacktrack();
  const createDialog = useDialog();
  const currentProjectId = useProjectId();
  const [projects, setProjects] = useState<HashMap<ProjectModel>>({});
  const [selected, setSelected] = useState<string>(currentProjectId);
  // The selected project may be restored after the view opened; never override a user pick.
  const userPickedRef = useRef(false);
  const projectIdFromUrl = useAppUrlParams(UrlParamName.PROJECT_ID);

  const {
    items: listedProjects,
    loading: projectsLoading,
    syncFailed: projectsSyncFailed,
  } = useProjects();
  const { reloadShowingLoading: reloadProjects } = useProjectsActions();
  const { selectProject } = useProjectActions();
  const { organizationsMap, loading: organizationsLoading } = useOrganizations();
  const { refresh: refreshOrganizations } = useOrganizationsActions();
  const isOffline = useIsOffline();
  // Under slow network, org sync flips loading on remounts. Only block the
  // picker until filtered projects (after permissions) are ready to render.
  // Use `projects` state — not listedProjects — so a non-empty API
  // list that filters to zero still shows an empty picker (no false spinner).
  const hasProjects = Object.keys(projects).length > 0;
  const hasOrganizations = organizationsMap.size > 0;
  const { status: syncStatus, restartMaxWait } = useProjectPickerSyncState({
    hasProjects,
    projectsLoading,
    projectsSyncFailed,
    hasOrganizations,
    organizationsLoading,
    isOffline,
  });
  const loading = syncStatus === 'loading';

  const retrySync = useCallback((): void => {
    restartMaxWait();
    reloadProjects();
    // Offline a download could not run.
    if (!isOffline) {
      refreshOrganizations();
    }
  }, [restartMaxWait, reloadProjects, refreshOrganizations, isOffline]);

  const wasOfflineRef = useRef(isOffline);
  useEffect(() => {
    const cameBackOnline = wasOfflineRef.current && !isOffline;
    wasOfflineRef.current = isOffline;
    if (cameBackOnline && !hasProjects) {
      restartMaxWait();
      reloadProjects();
    }
  }, [isOffline, hasProjects, restartMaxWait, reloadProjects]);

  const select = useCallback((): void => {
    if (selected) {
      const selectedProject = projects[selected];
      if (!currentProjectId) {
        selectionConfirmationDialog(
          createDialog,
          selectedProject._id
        );
      } else {
        changeConfirmationDialog(
          createDialog,
          selectedProject._id
        );
      }
    }
  }, [projects, selected, currentProjectId, createDialog]);

  useEffect(() => {
    const projectsToDisplay = filterProjectsForUser({
      projects: listedProjects,
      permissions: currentUser.permissions,
      basePermissions: currentUser.basePermissions,
      organizationsMap,
    });

    setProjects(transformArrayToHashMap<ProjectModel>(projectsToDisplay));
    setSelected((selected) => selected || projectsToDisplay[0]?._id || '');

    if (shouldAutoSelect(projectsToDisplay.length)) {
      void selectProject(projectsToDisplay[0]._id);
    }
  }, [listedProjects, currentUser.permissions, currentUser.basePermissions, organizationsMap, selectProject]);

  useEffect(() => {
    if (
      !currentProjectId &&
      !loading &&
      projectIdFromUrl &&
      projects[projectIdFromUrl]
    ) {
      userPickedRef.current = true;
      setSelected(projectIdFromUrl);
      selectionConfirmationDialog(
        createDialog,
        projectIdFromUrl
      );
    }
  }, [
    createDialog,
    currentProjectId,
    projectIdFromUrl,
    projects,
    loading,
  ]);

  useEffect(() => {
    if (userPickedRef.current || !currentProjectId) {
      return;
    }
    setSelected(currentProjectId);
  }, [currentProjectId]);

  const handleChange = useCallback((_: any, value: string): void => {
    userPickedRef.current = true;
    setSelected(value);
  }, []);

  const cancel = useCallback((): void => {
    if (currentProjectId) {
      back();
    }
  }, [currentProjectId, back]);

  const anyProjectSelected = Boolean(currentProjectId);

  if (loading) {
    return (
      <Spinner
        reason={`SelectProjectView projects loading: ${loading}, organizationsLoading: ${organizationsLoading}, projectsLoading: ${projectsLoading}`}
      />
    );
  }

  debugLog('organizationsWithProjectsMap', organizationsMap);
  const disabled = !selected || selected === currentProjectId;

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f2f2f2',
      }}
    >
      <div
        style={{
          backgroundColor: '#f2f2f2',
          paddingTop: '2rem',
          paddingBottom: '1rem',
          alignSelf: 'center',
        }}
      >
        <CreateProjectButton />
      </div>

      {(syncStatus === 'offline' || syncStatus === 'syncFailed') && (
        <Alert
          severity='warning'
          sx={{ alignSelf: 'center', marginBottom: '1rem' }}
          action={
            syncStatus === 'syncFailed' ? (
              <Button color='inherit' size='small' onClick={retrySync}>
                <FormattedMessage id={CommonTranslationKeys.refresh} />
              </Button>
            ) : undefined
          }
        >
          {syncStatus === 'offline' ? (
            <FormattedMessage
              id={NotificationTranslationKeys.noInternetConnection}
            />
          ) : (
            <FormattedMessage
              id={NotificationTranslationKeys.somethingWentWrong}
            />
          )}
        </Alert>
      )}

      <MemoSelectProjectForm
        projects={Object.values(projects).sort(compareProjectNames)}
        organizationsMap={organizationsMap}
        selected={selected}
        handleChange={handleChange}
        onSubmit={select}
        onCancel={cancel}
        submitDisabled={disabled}
        canCancel={anyProjectSelected}
      />
    </Box>
  );
}

export const SelectProject = composeHOCs(
  withRedirect,
  withCreateProject
)(SelectProjectView);
